// find-cached-item.ts — locate a FULL-NESTED object's item_arr inside the client caches
// (window.FateTide.casterCache / objectCache) so openItemInfo() can render a nested
// spell/ability/effect/status-template card INSTANTLY, with zero DB/network.
//
// WHY: a creature/PC caster blob already carries its nested abilities/spells/statuses FULLY
// nested (items + item_layer_types + computed) when full_nest=1. ft_dedupe_full_nests_to_shells()
// collapses DUPLICATE refs to bare shells {items:{item_id,item_name,item_type}} (no
// item_layer_types), and taxonomy refs (size/role/creature_type) are always shells. So we walk
// the caches and return the FIRST node that both matches the id AND is FULL (has its own
// item_layer_types). A shell-only / missing id returns null → caller falls back to the server.
//
// Mirrors the PHP/TS reader conventions: item identity via itemsCol (item_field), carrier descent
// via defaultProviderCarriers, UUID extraction via layerListEntryUuid.

import {
    itemsCol, layerListEntryUuid, defaultProviderCarriers, layerHas,
    inventoryField, layerOf, withCarrierRefsUnresolved,
} from '../shared/array-types';

export type CachedItemNode = Record<string, unknown>;

type CacheMapLike = { values(): Iterable<unknown> };

interface ObjectCardCacheRoot {
    casterCache?: unknown;
    objectCache?: unknown;
    encounterCache?: unknown;
}

function recordOf(value: unknown): CachedItemNode | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as CachedItemNode
        : null;
}

function cacheMapOf(value: unknown): CacheMapLike | null {
    const record = recordOf(value);
    return record && typeof record.values === 'function'
        ? record as unknown as CacheMapLike
        : null;
}

function cacheRoot(): ObjectCardCacheRoot {
    return (window as unknown as { FateTide?: ObjectCardCacheRoot }).FateTide ?? {};
}

// Some owner-only tools ship a page-local JSON island instead of populating the long-lived
// FateTide caches. Treat those islands as ordinary lookup roots so catalogized carrier refs
// (weapon/armor classifications, equipment slots, etc.) resolve through the same canonical
// ftFindFullNestedItem path. The creator island is assigned before its feature bundle runs.
export function pageIslandRoots(): CachedItemNode[] {
    const host = window as unknown as { ftCreatorCharacter?: unknown };
    const creator = recordOf(host.ftCreatorCharacter);
    return creator ? [creator] : [];
}

function cachedRoots(names: ReadonlyArray<keyof ObjectCardCacheRoot> = ['casterCache', 'objectCache', 'encounterCache']): CachedItemNode[] {
    const ft = cacheRoot();
    const roots: CachedItemNode[] = [];
    for (const name of names) {
        const cache = cacheMapOf(ft[name]);
        if (!cache) continue;
        for (const value of cache.values()) {
            const root = recordOf(value);
            if (root) roots.push(root);
        }
    }
    roots.push(...pageIslandRoots());
    return roots;
}

// DFS walkers below consume their stack with pop(). Reverse the roots once so the declared cache
// order remains the lookup priority: a freshly fetched per-caster blob must beat an older copy of
// the same mutable inventory instance still present in objectCache / encounterCache. Without this,
// volatile state such as current_item_charges can repaint from a stale encounter snapshot.
function rootPriorityStack(roots: CachedItemNode[]): unknown[] {
    return roots.slice().reverse();
}

function pushNestedChildren(node: CachedItemNode, stack: unknown[]): void {
    const ilt = recordOf(node.item_layer_types);
    if (ilt) {
        for (const slot of Object.values(ilt)) {
            if (slot !== null && typeof slot === 'object') stack.push(slot);
        }
    }
    // Object carriers only: a bare-UUID ref's def is reached by this same walk (root / riding catalogs, the creator
    // island) — resolving it here started a NESTED full walk per unresolvable ref (withCarrierRefsUnresolved).
    for (const carrier of withCarrierRefsUnresolved(() => defaultProviderCarriers(node))) {
        if (carrier !== null && typeof carrier === 'object') stack.push(carrier);
    }
    for (const [key, value] of Object.entries(node)) {
        if (key === 'item_layer_types' || SKIP_KEYS.has(key)) continue;
        if (value !== null && typeof value === 'object') stack.push(value);
    }
}

// A node is a FULL item_arr (not a bare shell) when it carries its own non-empty
// item_layer_types bucket AND a resolvable item_type. Shells carry only an `items` bucket.
function isFull(node: unknown): boolean {
    const record = recordOf(node);
    if (!record) return false;
    const ilt = record.item_layer_types;
    if (ilt === null || typeof ilt !== 'object' || Array.isArray(ilt)) return false;
    if (Object.keys(ilt).length === 0) return false;
    return !!itemsCol(record, 'item_type');
}

// Node identity — uppercased item_id, with the layer-entry UUID extractor as a fallback for
// rich/shelled entry shapes. Returns '' when no id is resolvable.
function nodeId(node: unknown): string {
    const direct = itemsCol<string>(node, 'item_id');
    if (direct) return String(direct).toUpperCase();
    const viaEntry = layerListEntryUuid(node);
    return viaEntry ? String(viaEntry).toUpperCase() : '';
}

// An inventory INSTANCE node — carries the `inventory` identity bucket (inventory_id, possess_id, …)
// on top of the template's items/item_layer_types. Identity is its inventory_id, NOT its template
// items.item_id (the same template id repeats across many inventory rows), so it must be matched and
// rendered as an inventory, never as the bare template.
export function isInventoryNode(node: unknown): boolean {
    const record = recordOf(node);
    if (!record) return false;
    const inv = recordOf(record.inventory);
    if (inv?.inventory_id !== undefined) return true;
    return record.inventory_id !== undefined;
}

// Uppercased inventory_id for an inventory node (bucketed first, flat-key fallback). '' when absent.
function nodeInventoryId(node: unknown): string {
    const record = recordOf(node);
    if (!record) return '';
    let v: unknown = inventoryField(node, 'inventory_id');
    if (v === undefined || v === null || v === '') {
        v = Array.isArray(record.inventory_id) ? record.inventory_id[0] : record.inventory_id;
    }
    return v ? String(v).toUpperCase() : '';
}

// A live runtime instance can carry the FULL layers of its catalog template while replacing
// presentation identity with instance state. The common example is a placed Canvas creature:
// items.item_id remains Canvas, but items.item_name is the encounter name (Malakith/Edrik). Such a
// node is addressable by encounter_creature_id / player-character identity, never by the template's
// item_id. Letting the generic item finder match it by items.item_id makes any bare Canvas reference
// inherit the merchant/actor's name, layers and mutable state. Inventory instances already obey this
// rule via isInventoryNode/nodeInventoryId; keep every runtime identity space disjoint here.
function isRuntimeItemInstance(node: unknown): boolean {
    const record = recordOf(node);
    if (!record) return false;
    if (isInventoryNode(record)) return true;
    const ec = recordOf(record.encounter_creatures);
    if (ec?.encounter_creature_id !== undefined || record.encounter_creature_id !== undefined) return true;
    if (recordOf(record.player_characters) !== null) return true;
    return record.status_id !== undefined || record.status_i !== undefined;
}

// Keys we never recurse into. `items` is the scalar identity bucket and `pictures` holds picture
// rows — neither contains nestable item_arrs. We do NOT skip `computed`: the finalized caster blob
// stores compiled abilities/spells/natural-weapons FULL-nested under computed.creature.* (e.g.
// computed.creature.skill_ability.quick_ability[0]), which is the ONLY full copy for many PC/creature
// sub-items — skipping it made findFullNestedItem miss them and fall back to the server.
const SKIP_KEYS = new Set<string>(['items', 'pictures']);

// Transient same-task memo (2026-06-12): cache mutation only ever happens in async handlers
// (fetch callbacks, WS pushes), so within one synchronous render pass repeated lookups of the
// same id — the ability-row accessors resolve the same ref 4–5× per row, and an identity-only
// catalog ref walks EVERY cached blob on each miss — can safely share one answer. The memo
// clears when the microtask queue drains, so it never outlives the synchronous pass that
// filled it (no staleness across cache replacements, which are always async).
const _sameTaskMemo = new Map<string, CachedItemNode | null>();
let _sameTaskMemoArmed = false;
function _sameTaskMemoSet(key: string, val: CachedItemNode | null): void {
    if (!_sameTaskMemoArmed) {
        _sameTaskMemoArmed = true;
        const clear = (): void => { _sameTaskMemo.clear(); _sameTaskMemoArmed = false; };
        if (typeof queueMicrotask === 'function') queueMicrotask(clear);
        else Promise.resolve().then(clear);
    }
    _sameTaskMemo.set(key, val);
}

// Miss memo across tasks (2026-09-10): a MISS is the expensive answer — the catalog fast path fails
// and the DFS below walks every cached root (an encounter blob of 1,141 inventory rows: megabytes)
// before returning null, and the same-task memo above forgets it at the next microtask. The status
// reader and the carrier-ref resolver ask the same unresolvable ids (layerless status defs, ids no
// root carries) on every encounter echo and every projection, so those walks repeated per row per
// echo (~1.3 s per echo in the wooded Annex, CPU profile 2026-09-10). A miss is remembered against
// the ROOTS EPOCH — the identity of every cached root plus each root's `_ws_version` — and is
// re-walked when a root changes or its asynchronously compiled inventory arrives.
const _missEpochMemo = new Map<string, string>();
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function')
    window.addEventListener('ft:inventory-base-compiled', () => { _missEpochMemo.clear(); _sameTaskMemo.clear(); });
const _rootIdentity = new WeakMap<object, number>();
let _rootSequence = 0;
function rootsEpochKey(roots: readonly CachedItemNode[]): string {
    // A caster row that is ALIASED inside an encounter blob's roster contributes its identity only,
    // not its `_ws_version`: every legacy action that patches a member row also bumps the encounter
    // blob's version in the same flush (process_affected_ids folds the creature diff into the
    // encounter delta), so the blob's version already re-walks for it — counting the row's own bump
    // too made one move cost TWO walks per unresolvable id (caster delta, then encounter delta).
    const aliasedRows = new WeakSet<object>();
    for (const root of roots) {
        const roster = (root as { encounter_creatures_arr?: unknown }).encounter_creatures_arr;
        if (!Array.isArray(roster)) continue;
        for (const row of roster) if (row !== null && typeof row === 'object') aliasedRows.add(row);
    }
    let key = '';
    for (const root of roots) {
        let ordinal = _rootIdentity.get(root);
        if (ordinal === undefined) { ordinal = ++_rootSequence; _rootIdentity.set(root, ordinal); }
        const version = (root as { _ws_version?: unknown })._ws_version;
        key += aliasedRows.has(root) ? `${ordinal},` : `${ordinal}:${typeof version === 'number' ? version : ''},`;
    }
    return key;
}

/**
 * Walk every entry of the client caches looking for a FULL copy of `id`. Returns the matching
 * item_arr node, or null when only a shell (or nothing) is cached. First full hit wins; a shell
 * seen earlier never short-circuits. `{ walk: false }` answers from the catalog / root-direct fast
 * paths only (window.ftFindCachedFullItemNoWalk) — every live-data hit comes from those.
 */
export function findCachedFullItemNoWalk(id: string): CachedItemNode | null {
    return findFullNestedItem(id, { walk: false });
}

export function findFullNestedItem(id: string, options: { walk: boolean } = { walk: true }): CachedItemNode | null {
    if (!id) return null;
    const target = String(id).toUpperCase();
    const memoized = _sameTaskMemo.get(target);
    if (memoized !== undefined) return memoized;

    const roots = cachedRoots();
    // encounterCache too (2026-06-11): the catalog UNIONS up to the ENCOUNTER root — member
    // creature blobs re-seeded into casterCache from an encounter push carry NO catalog of their
    // own, so by-id resolution must also walk the encounter roots (where the defs actually live).
    if (roots.length === 0) return null;

    // Catalog-as-store fast path (2026-06-12): since Round 8 the root `.catalog` IS the master
    // def store (tree = bare refs; any in-tree full def of a catalogizable type is an audited
    // leak), so for catalogized ids the DFS below only re-finds root.catalog[id] after walking
    // megabytes of refs — measured 1.5–9ms per call against a full encounter blob, at ~880
    // finder calls per sheet tab. Direct-key every root's catalog first; same key tries as
    // catalogShell (raw / upper / lower — the catalog keeps the raw item_id case). Identity-only
    // rows, picture entries, and inventory instances fail the isFull/identity gates and fall
    // through to the walk, preserving the pre-fast-path result for every non-def id space.
    for (const root of roots) {
        const cat = recordOf(root.catalog);
        if (!cat) continue;
        const entry = recordOf(cat[id] ?? cat[target] ?? cat[String(id).toLowerCase()]);
        if (entry && !isRuntimeItemInstance(entry) && isFull(entry) && nodeId(entry) === target) {
            _sameTaskMemoSet(target, entry);
            return entry;
        }
    }

    // Root-direct match (2026-09-10): a fetched definition is its OWN objectCache root, so it used to
    // be reached only by the DFS below — after walking every caster row that sorts ahead of it.
    // Check the roots themselves first; nested leaks still fall through to the walk.
    for (const root of roots) {
        if (isFull(root) && !isRuntimeItemInstance(root) && nodeId(root) === target) {
            _sameTaskMemoSet(target, root);
            return root;
        }
    }

    // `walk: false` (a cosmetic reader that must never pay a full walk): fast paths only. The miss is NOT memoized,
    // so a walking caller in the same task still gets its walk.
    if (!options.walk) return null;

    const epoch = rootsEpochKey(roots);
    if (_missEpochMemo.get(target) === epoch) {
        _sameTaskMemoSet(target, null);
        return null;
    }

    const seen = new WeakSet<object>();
    const stack: unknown[] = rootPriorityStack(roots);
    // ⚠ Do NOT skip descending into `root.catalog` here (tried 2026-09-10, reverted the same hour):
    // catalog defs still carry FULL nested carriers (a creature_sub_type's natural weapons), and the
    // action bar resolves those only through this walk — skipping the catalog emptied the Reach
    // ability from the dock.

    while (stack.length) {
        const node = stack.pop();
        if (node === null || typeof node !== 'object') continue;

        if (Array.isArray(node)) {
            for (const child of node) {
                if (child !== null && typeof child === 'object') stack.push(child);
            }
            continue;
        }

        const record = recordOf(node);
        if (!record || seen.has(record)) continue;
        seen.add(record);

        // Match: this node IS the item we want, and it's a full copy. Inventory instances match ONLY
        // by inventory_id (their template items.item_id collides across rows + with the bare template),
        // and non-inventory nodes match ONLY by items.item_id — disjoint id spaces, no cross-matching.
        if (isFull(record)) {
            if (isInventoryNode(record)) {
                if (nodeInventoryId(record) === target) { _sameTaskMemoSet(target, record); return record; }
            } else if (!isRuntimeItemInstance(record) && nodeId(record) === target) {
                _sameTaskMemoSet(target, record);
                return record;
            }
        }

        // Recurse: layer buckets (entry | array | scalar), carrier descent, and any other
        // nested object/array members (skip the leaf data buckets).
        pushNestedChildren(record, stack);
    }

    if (_missEpochMemo.size >= 4096) _missEpochMemo.clear();
    _missEpochMemo.set(target, epoch);
    _sameTaskMemoSet(target, null);
    return null;
}

/**
 * Walk a root blob (a caster/object cache entry) and collect the uppercased inventory_id of every
 * inventory INSTANCE nested anywhere inside it — the creature's pack, nested containers, equipped
 * slots. Used by the side-info subscribe glue (open-side-info.ts): when a caster_update lands we
 * need to know whether the inventory the side-info drawer is showing belongs to that caster (its
 * id will appear here) before deciding to re-render. Mirrors findFullNestedItem's descent (same
 * SKIP_KEYS / carrier rules) but collects instead of matching, and does NOT require full nodes —
 * a shell inventory row still has an inventory_id worth matching.
 */
export function collectInventoryIds(blob: unknown): string[] {
    const out: string[] = [];
    if (blob === null || typeof blob !== 'object') return out;

    const seen = new WeakSet<object>();
    const stack: unknown[] = [blob];

    while (stack.length) {
        const node = stack.pop();
        if (node === null || typeof node !== 'object') continue;

        if (Array.isArray(node)) {
            for (const child of node) {
                if (child !== null && typeof child === 'object') stack.push(child);
            }
            continue;
        }

        const record = recordOf(node);
        if (!record || seen.has(record)) continue;
        seen.add(record);

        if (isInventoryNode(record)) {
            const id = nodeInventoryId(record);
            if (id) out.push(id);
        }

        pushNestedChildren(record, stack);
    }

    return out;
}

/**
 * Walk a root blob and collect the uppercased status_id of every status INSTANCE nested anywhere
 * inside it (statuses.caster / statuses.target on the blob and any nested creature/inventory row).
 * The side-info subscribe glue (open-side-info.ts) unions these into the affected-id set so a
 * caster_update that touches a status's owner can re-render an OPEN status card in place — the card
 * is registered under its status_id (template.js:openItemInfo), which a caster/inventory-id-only set
 * would never match. Generic deep walk with a WeakSet guard; status instances never live on catalog
 * template entries (those key on item_id), so no false ids are collected.
 */
export function collectStatusIds(blob: unknown): string[] {
    const out: string[] = [];
    if (blob === null || typeof blob !== 'object') return out;

    const seen = new WeakSet<object>();
    const stack: unknown[] = [blob];

    while (stack.length) {
        const node = stack.pop();
        if (node === null || typeof node !== 'object') continue;

        if (Array.isArray(node)) {
            for (const child of node) {
                if (child !== null && typeof child === 'object') stack.push(child);
            }
            continue;
        }

        const record = recordOf(node);
        if (!record || seen.has(record)) continue;
        seen.add(record);

        const raw = record.status_id;
        const sid = Array.isArray(raw) ? raw[0] : raw;
        if (typeof sid === 'string' && sid !== '') out.push(sid.toUpperCase());

        for (const v of Object.values(record)) {
            if (v !== null && typeof v === 'object') stack.push(v);
        }
    }

    return out;
}

// ── thinned-inventory-instance rehydration ───────────────────────────────────────
// An encounter/caster blob de-dupes inventory instances that SHARE a template (e.g. the four party
// chests all use the one "chest" item): the compile keeps ONE full copy of the template def in the
// root `catalog` (keyed by item_id) and THINS the duplicate instances down to just their
// instance-specific data — `inventory` (identity + overrides), `computed`, `position`, `permissions`,
// `inventory_in_container`. A thinned instance carries NO `items` bucket and NO `item_layer_types`, so
// findFullNestedItem (which requires isFull) returns null for it → the side panel falls back to the
// server. Rehydration resolves the thinned instance by inventory_id and merges its template def from
// the catalog, so the card renders client-side with zero DB. See resolveCachedItemForRender below.

// True when a node already carries its OWN template layers (a full instance — nothing to rehydrate).
function hasOwnLayers(node: unknown): boolean {
    const record = recordOf(node);
    if (!record) return false;
    const ilt = record.item_layer_types;
    return ilt !== null && typeof ilt === 'object' && !Array.isArray(ilt)
        && Object.keys(ilt).length > 0 && !!itemsCol(node, 'item_type');
}

// Walk the client caches for the FIRST inventory INSTANCE whose inventory_id matches `invId` — UNLIKE
// findFullNestedItem this does NOT require isFull, so it also finds thinned instances (their template
// layers live in the catalog, not on the node). Mirrors the standard descent (SKIP_KEYS / carriers).
// Exported (2026-06-15): the breakdown provenance tree (source-line-block.ts) resolves an
// inventory_id `source` crumb to its inventory_name + computed.enchantment off this raw instance node.
/** Adopt confirmed container contents into every cached copy before a deliberate repaint. */
export function syncCachedInventoryContents(freshRoot: unknown, invId: string): number {
    const target = String(invId).toUpperCase();
    const matches = (roots: unknown[]): CachedItemNode[] => {
        const found: CachedItemNode[] = [];
        const seen = new WeakSet<object>();
        const stack = roots.slice();
        while (stack.length) {
            const value = stack.pop();
            if (!value || typeof value !== 'object' || seen.has(value)) continue;
            seen.add(value);
            if (Array.isArray(value)) { stack.push(...value); continue; }
            const node = recordOf(value);
            if (!node) continue;
            if (recordOf(node.inventory) && isInventoryNode(node) && nodeInventoryId(node) === target) found.push(node);
            pushNestedChildren(node, stack);
        }
        return found;
    };
    const fresh = matches([freshRoot])[0];
    if (!fresh) return 0;
    const copies = matches(cachedRoots());
    for (const node of copies) {
        if ('inventory_in_container' in fresh) node.inventory_in_container = fresh.inventory_in_container;
        else delete node.inventory_in_container;
        const computed = recordOf(node.computed) ?? {};
        const freshComputed = recordOf(fresh.computed) ?? {};
        for (const key of ['weight', 'cost']) {
            if (key in freshComputed) computed[key] = freshComputed[key];
            else delete computed[key];
        }
        node.computed = computed;
        if (fresh.permissions) node.permissions = fresh.permissions;
    }
    _sameTaskMemo.delete(target);
    return copies.length;
}

export function findInventoryInstanceNode(invId: string): CachedItemNode | null {
    if (!invId) return null;
    const target = String(invId).toUpperCase();
    const roots = cachedRoots();
    if (roots.length === 0) return null;

    const seen = new WeakSet<object>();
    const stack: unknown[] = rootPriorityStack(roots);
    while (stack.length) {
        const node = stack.pop();
        if (node === null || typeof node !== 'object') continue;
        if (Array.isArray(node)) {
            for (const child of node) { if (child !== null && typeof child === 'object') stack.push(child); }
            continue;
        }
        const record = recordOf(node);
        if (!record || seen.has(record)) continue;
        seen.add(record);

        // A real instance carries its template id (thinned) or its own layers (full). Encounter side
        // tables also key by a flat inventory_id — difficult_terrain_boxes / _footprints — and sort after
        // encounter_inventories_arr, so the DFS pops them FIRST; matching one hid every difficult-terrain
        // object (rubble, brush) from the card and emptied its scene radial (owner 2026-09-22).
        if (isInventoryNode(record) && nodeInventoryId(record) === target
            && (hasOwnLayers(record) || inventoryTemplateId(record) !== '')) return record;

        pushNestedChildren(record, stack);
    }
    return null;
}

// The catalog template id of an inventory instance (bucketed first, flat-key fallback). '' when absent.
function inventoryTemplateId(record: CachedItemNode): string {
    let tplId: unknown = inventoryField(record, 'inventory_item_id');
    if (tplId === undefined || tplId === null || tplId === '') {
        tplId = Array.isArray(record.inventory_item_id) ? record.inventory_item_id[0] : record.inventory_item_id;
    }
    return tplId === undefined || tplId === null ? '' : String(tplId);
}

/**
 * Rehydrate a thinned inventory instance: merge in the template def (items + item_layer_types +
 * pictures) resolved from the root catalog by inventory_item_id. Returns a NEW merged object (never
 * mutates a cache node): template fields fill in what the instance lacks, every key the instance owns
 * (inventory / computed / position / permissions / inventory_in_container) wins. Returns the node
 * as-is when it's already full, or null when it's thinned AND its template can't be resolved (so the
 * caller falls back to the server rather than render a layerless husk).
 */
function rehydrateInventoryInstance(instance: unknown): CachedItemNode | null {
    const record = recordOf(instance);
    if (!record) return null;
    if (hasOwnLayers(record)) return record;

    const tplId = inventoryTemplateId(record);
    if (tplId === '') return null;

    const tpl = findFullNestedItem(tplId);
    if (tpl === null || typeof tpl !== 'object') return null;

    // Template first, instance second → instance overrides identity/contents/computed/permissions,
    // template supplies items + item_layer_types + pictures the thinned instance dropped.
    const templateComputed = recordOf(tpl.computed);
    const instanceComputed = recordOf(record.computed);
    const templateVitality = recordOf(templateComputed?.vitality);
    const instanceVitality = recordOf(instanceComputed?.vitality);
    const merged: CachedItemNode = {
        ...tpl,
        ...record,
    };
    if (templateComputed || instanceComputed) {
        const computed: CachedItemNode = {
            ...recordOf(tpl.computed),
            ...recordOf(record.computed),
        };
        // computed is sparse on inventory instances in v38: preserve catalog PL/vitality
        // defaults while allowing damage and every other instance value to win. Do not invent
        // an empty vitality bucket for ordinary items that have no vitality contract.
        if (templateVitality || instanceVitality) {
            computed.vitality = {
                ...templateVitality,
                ...instanceVitality,
            };
        }
        // v39: cost/weight totals may be catalog defaults while a container keeps other
        // instance-owned fields in the same bucket. Merge those buckets one level deeper so the
        // instance spread cannot hide the template total.
        for (const bucket of ['cost', 'weight'] as const) {
            const templateBucket = recordOf(templateComputed?.[bucket]);
            const instanceBucket = recordOf(instanceComputed?.[bucket]);
            if (templateBucket || instanceBucket) {
                computed[bucket] = { ...templateBucket, ...instanceBucket };
            }
        }
        merged.computed = computed;
    }
    return merged;
}

// ── enchantment-template synthesis (2026-06-15) ──────────────────────────────────
// An enchantment template is NOT catalogized (it's derived data) — so resolveCachedItemForRender
// can't find it by id. But every equipped item carries its enchantment FULLY on its inventory
// instance node: computed.enchantment (template id + composite name + member abilities) plus the raw
// enchantment_template_enchantments rows. Synthesize a renderable enchantment_template item_arr from
// that cached block so openItemInfo paints it client-side (no DB) — the breakdown's enchantment crumb
// drills in instantly instead of falling back to handle_enchantment_templates(), which rebuilds the
// template from the database. Mirrors the server's handle_enchantment_templates output shape (an
// item_arr with item_type 'enchantment_template' whose layers ARE the member enchantments).

const _ENCH_UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

// computed.enchantment on a node, normalized to an array (single object today; array-tolerant).
function enchantmentBlocksOf(node: unknown): CachedItemNode[] {
    const computed = recordOf(recordOf(node)?.computed);
    const e = computed?.enchantment;
    if (Array.isArray(e)) return e.map(recordOf).filter((entry): entry is CachedItemNode => entry !== null);
    const block = recordOf(e);
    return block ? [block] : [];
}

function firstScalar(v: unknown): string {
    return String(Array.isArray(v) ? v[0] : (v ?? ''));
}

// Build a renderable enchantment_template item_arr from an inventory instance's cached enchantment
// `block`. Each member enchantment is embedded FULL (resolved from the caches) when available so its
// name / icon / PL render inline; otherwise the bare id (list-display resolves it from cache too).
// Members come from the raw enchantment_template_enchantments rows (every member TYPE), falling back
// to computed.enchantment.abilities (ability-typed members only). Null when no usable identity.
function synthesizeEnchantmentTemplateBlob(invNode: CachedItemNode, block: CachedItemNode): CachedItemNode | null {
    const id = firstScalar(block.enchantment_template_id);
    if (!id || !_ENCH_UUID_RE.test(id)) return null;
    const name = firstScalar(block.item_name) || 'Enchantment';

    const layers: Record<string, unknown[]> = {};
    const pushMember = (type: string, memberId: string): void => {
        if (!type || !memberId || !_ENCH_UUID_RE.test(memberId)) return;
        const full = findFullNestedItem(memberId);
        (layers[type] ??= []).push(full ?? memberId);
    };

    const rows = invNode.enchantment_template_enchantments;
    if (Array.isArray(rows)) {
        for (const row of rows) {
            const r = recordOf(row);
            pushMember(String(r?.enchantment_type ?? ''), String(r?.enchantment_item_id ?? ''));
        }
    }
    if (Object.keys(layers).length === 0) {
        const abilities = recordOf(block.abilities);
        if (abilities) {
            for (const t of Object.keys(abilities)) {
                const arr = Array.isArray(abilities[t]) ? abilities[t] : [];
                for (const entry of arr) {
                    const member = recordOf(entry);
                    pushMember(t, String(member?.item_id ?? member?.ability_id ?? ''));
                }
            }
        }
    }
    if (Object.keys(layers).length === 0) return null;

    return {
        items: { item_id: id, item_name: name, item_type: 'enchantment_template' },
        item_layer_types: layers,
    };
}

// Walk the client caches for the FIRST inventory instance carrying computed.enchantment whose
// enchantment_template_id === `enchId`, and synthesize its enchantment_template card. Mirrors the
// standard descent (SKIP_KEYS / carriers). Null when no equipped item in cache bears that enchantment.
export function findEnchantmentTemplateBlob(enchId: string): CachedItemNode | null {
    if (!enchId || !_ENCH_UUID_RE.test(enchId)) return null;
    const target = enchId.toUpperCase();
    const roots = cachedRoots();
    if (roots.length === 0) return null;

    const seen = new WeakSet<object>();
    const stack: unknown[] = rootPriorityStack(roots);
    while (stack.length) {
        const node = stack.pop();
        if (node === null || typeof node !== 'object') continue;
        if (Array.isArray(node)) {
            for (const child of node) { if (child !== null && typeof child === 'object') stack.push(child); }
            continue;
        }
        const record = recordOf(node);
        if (!record || seen.has(record)) continue;
        seen.add(record);

        for (const block of enchantmentBlocksOf(record)) {
            if (firstScalar(block.enchantment_template_id).toUpperCase() === target) {
                const synth = synthesizeEnchantmentTemplateBlob(record, block);
                if (synth) return synth;
            }
        }

        pushNestedChildren(record, stack);
    }
    return null;
}

/**
 * Resolve the cache node to RENDER for `id` (render-from-cache.ts). Tries findFullNestedItem first
 * (full nested objects + full inventory instances + templates); on a miss, resolves a THINNED
 * inventory instance by inventory_id and rehydrates it from the catalog template; finally synthesizes
 * an enchantment_template card from a cached equipped item's computed.enchantment block. Returns null
 * when no path resolves — the caller then falls back to the server (no regression).
 */
export function resolveCachedItemForRender(id: string): CachedItemNode | null {
    const full = findFullNestedItem(id);
    if (full) return full;
    const instance = findInventoryInstanceNode(id);
    if (instance) return rehydrateInventoryInstance(instance);
    const ench = findEnchantmentTemplateBlob(id);
    if (ench) return ench;
    return null;
}

// Item types that the generic object card (renderObjectCard) faithfully renders. Everything else
// (creatures, player_characters, calendars, status INSTANCES, campaign_house_rule) keeps the
// current server path. We gate on the found blob, not just the type string, so we can detect a
// status-effect INSTANCE (carries a status_id/status_i layer) vs a status-effect TEMPLATE item.
//
// class / subclass are CACHE-ELIGIBLE (2026-06-15): their full def rides on the caster blob's
// `.catalog` (verified: primary/spellcasting essences, vitality dice, description all present), and
// the SIDE INFO panel wants the plain info card — name + description + the layer property list
// (renderItemListDisplay) — NOT the item.php-page class table (table_item_class_inc.php) with its
// subclass picker / level selects, which is a full-page concern and intentionally not surfaced here.
// So they render instantly from cache like any other object; the heavy PHP class table is never hit.
const INELIGIBLE_TYPES = new Set<string>([
    'creature', 'player_character', 'calendar',
    'campaign_house_rule_type', 'encounter', 'location', 'campaign',
]);

/**
 * True when `blob` should be rendered by the client object card. False → caller falls back to the
 * server (which routes to the specialized partial for that type).
 */
export function isObjectCardEligibleType(blob: unknown): boolean {
    const record = recordOf(blob);
    if (!record) return false;
    const type = String(itemsCol(record, 'item_type') ?? '').toLowerCase();
    if (!type) return false;
    if (INELIGIBLE_TYPES.has(type)) return false;
    // Status-effect INSTANCE (an applied status, not the library template) → server path; it owns
    // caster/target/origin/timeline rendering (table_item_status_effect_inc.php).
    if (record.status_id !== undefined || record.status_i !== undefined || layerHas(record, 'status_id')) {
        return false;
    }
    // In-encounter creature handled above by type, but guard the EC bucket too.
    if (recordOf(record.encounter_creatures)?.encounter_creature_id) return false;
    return true;
}

// ── inventory render plan (cache vs server + which gated controls to show) ───────
// Most inventory clicks render fully from the cached full-nest node with zero DB. A handful of
// controls in table_item_object_inc.php depend on data the blob does NOT carry; when one applies we
// flag `server: true` and openItemInfo falls back to the server path (full, correct control set).
// Everything else stays instant. Kept in sync with the PHP gates:
//   - GM/admin view  → rubble eligibility, container loot RNG, encounter-link travel, GM translate,
//                       light-source toggles (all DB / server-only)
//   - book reading   → read/unread needs the finish-reading roll-log row + language checks (DB)
//   - encounter link → resolves linked inventory + possessor + travel gate (DB)
//   - locked         → lockpick needs the lock-instance row + caster tool resolution (DB)
//   - gathering      → gather/skin needs the roll pipeline + possessor walk (server)
//   - disenchant     → never falls back: always cache-renders; the button shows only when the caster
//                      CARRIES the enchanting tools (see below).
export const LOCKED_STATUS_ITEM_ID = '477A7977-60D2-4B9C-96B0-6ED4BFD6437E';      // StatusEffect::getId('locked')
// The disenchant ability (FDCAAAC1-…)'s requires_tool → the "Enchanting Tools" item. The caster needs
// a stack of this (carried, or within encounter reach) to disenchant. We can check the CARRIED case
// from the cached caster blob; the within-reach case still needs the encounter/position data, so it
// falls back to the server.
const ENCHANTING_TOOLS_ITEM_ID = '1FBB3F1C-D81C-B925-B295-CC8FEAFAD0B7';
// Exported: render-creature-card.ts's blob skinning tab reuses the integrated-bit predicate.
export const INTEGRATED_OBJECT_ITEM_ID = '36C061BF-E480-4334-961C-26500150E3C4';   // StatusEffect::getId('integrated_object')
// Crafting Phase-3 WIP tracker on an "Unfinished Item" row — presence gates the craft_controls island.
export const CRAFTING_PROGRESS_STATUS_ITEM_ID = 'E49AA8BA-1F60-4FD8-B3C0-852BBA3978C3'; // StatusEffect::getId('crafting_progress')

function statusRowsOf(node: unknown, mode: string): unknown[] {
    const s = recordOf(recordOf(node)?.statuses)?.[mode];
    if (Array.isArray(s)) return s;
    if (s !== null && typeof s === 'object') return Object.values(s);
    return [];
}
export function nodeHasStatusItemId(node: unknown, mode: string, uuid: string): boolean {
    const want = uuid.toUpperCase();
    for (const row of statusRowsOf(node, mode)) {
        if (row === null || typeof row !== 'object') continue;
        const id = String(itemsCol(row, 'item_id') ?? recordOf(row)?.item_id ?? '').toUpperCase();
        if (id === want) return true;
    }
    return false;
}
// A caster root blob's encounter_creature_id (array- or scalar-shaped), uppercased.
function casterEcId(node: unknown): string {
    const raw = recordOf(recordOf(node)?.encounter_creatures)?.encounter_creature_id;
    const v = Array.isArray(raw) ? raw[0] : raw;
    return v ? String(v).toUpperCase() : '';
}

// Locate the caster root blob for `ecId` in the client caches (the one whose encounter_creature_id
// matches). Used to read the caster's carried inventory for the disenchant tool check. Null on miss.
function findCasterArrByEcId(ecId: string): CachedItemNode | null {
    if (!ecId) return null;
    const want = ecId.toUpperCase();
    for (const root of cachedRoots(['casterCache', 'objectCache'])) {
        if (casterEcId(root) === want) return root;
    }
    return null;
}

// Like findCasterArrByEcId, but ALSO checks the encounter roots' member lists — on
// ft3d_scene_host.php / the encounter map the active caster's blob usually lives at
// encounterCache[root].encounter_creatures_arr[i], not as its own casterCache root.
function findCasterNodeAnywhere(ecId: string): CachedItemNode | null {
    const direct = findCasterArrByEcId(ecId);
    if (direct) return direct;
    const want = ecId.toUpperCase();
    for (const root of cachedRoots(['encounterCache', 'casterCache', 'objectCache'])) {
        const members = root.encounter_creatures_arr;
        if (!Array.isArray(members)) continue;
        for (const ec of members) {
            const member = recordOf(ec);
            if (member && casterEcId(member) === want) return member;
        }
    }
    return null;
}

// ── key ↔ lock "JSON truth" lookup ────────────────────────────────────────────────
// The compile seam (build_inventory_arr.php) stamps computed.key_links.opens = [lock inventory
// ids] on EVERY key row, and that rides the cached caster blob — so whether the caster holds a
// key that fits a given lock is answerable entirely client-side, zero DB. useKeysOnLock
// (lockpick.ts) uses this to send the matched key EXPLICITLY (the server then only validates
// possession + the link row — no search), and to short-circuit a guaranteed miss without any
// AJAX at all. The relock button (render-object-card.ts) gates on the same lookup.
export interface CasterKeyLookup {
    /** true when the caster's blob was found in the caches — the answer is authoritative. */
    casterResolved: boolean;
    /** inventory_id of the first carried key whose opens[] hits a lock id; '' when none. */
    keyInventoryId: string;
    /** display name of that key ('' when none). */
    keyName: string;
}
export function findCasterKeyForLock(casterId: string, lockIds: Array<string | null | undefined>): CasterKeyLookup {
    const miss: CasterKeyLookup = { casterResolved: false, keyInventoryId: '', keyName: '' };
    if (!casterId) return miss;
    const want = new Set<string>();
    for (const id of lockIds) {
        if (typeof id === 'string' && id !== '') want.add(id.toUpperCase());
    }
    if (want.size === 0) return miss;

    const casterNode = findCasterNodeAnywhere(String(casterId));
    if (!casterNode) return miss;

    const result: CasterKeyLookup = { casterResolved: true, keyInventoryId: '', keyName: '' };
    // Recursive walk of the caster's carried inventory — keys can sit in a keyring/bag at any
    // depth. NOTE: caster blobs commonly list container CONTENTS flat in `inventory` too
    // (possess_id → the container), so the flat pass alone already covers most shapes.
    const walk = (rows: unknown): boolean => {
        if (!Array.isArray(rows)) return false;
        for (const row of rows) {
            if (row === null || typeof row !== 'object') continue;
            const rowRecord = recordOf(row);
            if (!rowRecord) continue;
            const opens = recordOf(recordOf(rowRecord.computed)?.key_links)?.opens;
            if (Array.isArray(opens)) {
                for (const lockId of opens) {
                    if (typeof lockId === 'string' && want.has(lockId.toUpperCase())) {
                        result.keyInventoryId = nodeInventoryId(rowRecord);
                        result.keyName = String(
                            inventoryField(rowRecord, 'inventory_name')
                            ?? itemsCol(rowRecord, 'item_name')
                            ?? '',
                        );
                        if (result.keyInventoryId) return true;
                    }
                }
            }
            if (walk(rowRecord.inventory_in_container ?? rowRecord.container_contents ?? null)) return true;
        }
        return false;
    };
    walk(casterNode.inventory ?? []);
    return result;
}

// ── optimistic lock-state stamp ───────────────────────────────────────────────────
// After use_key_inc.php confirms an unlock/relock, the cached blobs are stale until the WS
// recompile lands (~1s). That staleness broke the relock refresh both ways: the 900ms
// refreshSidePanel cache-painted the still-unlocked node, and once the push DID land the
// now-locked node hits the locked→server gate in computeInventoryRenderPlan, so the in-place
// re-render is a no-op. Stamping the confirmed state onto EVERY cached copy makes any
// subsequent render agree with the server: a stamped-locked node falls through to the server
// render (fresh lockpick/unlock controls), a stamped-unlocked node cache-paints open.
// The stamp is client-only and overwritten wholesale by the next WS blob swap.
function applyLockStamp(node: CachedItemNode, locked: boolean): void {
    const invId = nodeInventoryId(node);
    if (locked) {
        let statuses = recordOf(node.statuses);
        if (!statuses) {
            statuses = {};
            node.statuses = statuses;
        }
        for (const mode of ['caster', 'target']) {
            if (nodeHasStatusItemId(node, mode, LOCKED_STATUS_ITEM_ID)) continue;
            const row = {
                item_id: LOCKED_STATUS_ITEM_ID,
                status_caster_id: invId,
                status_target_id: invId,
                __optimistic: 1,
            };
            const bucket = statuses[mode];
            if (Array.isArray(bucket)) bucket.push(row);
            else {
                const bucketRecord = recordOf(bucket);
                if (bucketRecord) bucketRecord.__optimistic_locked = row;
                else statuses[mode] = [row];
            }
        }
    } else {
        const s = recordOf(node.statuses);
        if (!s) return;
        const isLockedRow = (row: unknown): boolean =>
            String(itemsCol(row, 'item_id') ?? recordOf(row)?.item_id ?? '').toUpperCase() === LOCKED_STATUS_ITEM_ID;
        for (const mode of ['caster', 'target']) {
            const bucket = s[mode];
            if (Array.isArray(bucket)) s[mode] = bucket.filter((row) => !isLockedRow(row));
            else {
                const bucketRecord = recordOf(bucket);
                if (!bucketRecord) continue;
                for (const k of Object.keys(bucketRecord)) {
                    if (isLockedRow(bucketRecord[k])) delete bucketRecord[k];
                }
            }
        }
    }
}

/** Stamp locked/unlocked onto every cached copy of inventory `invId`. Returns copies touched. */
export function stampCachedInventoryLockState(invId: string, locked: boolean): number {
    if (!invId) return 0;
    const target = String(invId).toUpperCase();
    const roots = cachedRoots();
    if (roots.length === 0) return 0;

    let stamped = 0;
    const seen = new WeakSet<object>();
    const stack: unknown[] = roots.slice();
    while (stack.length) {
        const node = stack.pop();
        if (node === null || typeof node !== 'object') continue;
        if (Array.isArray(node)) {
            for (const child of node) { if (child !== null && typeof child === 'object') stack.push(child); }
            continue;
        }
        const record = recordOf(node);
        if (!record || seen.has(record)) continue;
        seen.add(record);

        if (isInventoryNode(record) && nodeInventoryId(record) === target) {
            applyLockStamp(record, locked);
            stamped++;
            continue;
        }

        pushNestedChildren(record, stack);
    }
    return stamped;
}

// Port of the CARRIED branch of caster_required_item_resolution(): does the caster carry a stack
// (qty ≥ minQty) of `itemId`, in their inventory or any nested container? (We deliberately do NOT
// port the encounter-within-reach branch — that needs encounter/position data not in this blob.)
function casterCarriesItem(casterArr: unknown, itemId: string, minQty = 1): boolean {
    const want = itemId.toUpperCase();
    const need = Math.max(1, minQty);
    let found = false;
    const walk = (items: unknown): void => {
        if (!Array.isArray(items)) return;
        for (const it of items) {
            if (it === null || typeof it !== 'object') continue;
            const item = recordOf(it);
            if (!item) continue;
            const id = String(inventoryField(item, 'inventory_item_id') ?? '').toUpperCase();
            const qty = Number(inventoryField(item, 'item_qty') ?? 0);
            if (id === want && qty >= need) { found = true; return; }
            const sub = item.inventory_in_container ?? item.container_contents ?? [];
            walk(sub);
            if (found) return;
        }
    };
    walk(recordOf(casterArr)?.inventory ?? []);
    return found;
}

export interface InventoryRenderPlan {
    /** true → caller must use the server render path (a server-only control applies). */
    server: boolean;
    /** true → the cache render should paint the disenchant button (carried tools + valuable + owned). */
    showDisenchant: boolean;
}

const PLAN_CACHE_PLAIN: InventoryRenderPlan = { server: false, showDisenchant: false };

/**
 * Decide how to render an inventory blob: instantly from cache (optionally with the disenchant button)
 * or via the server. Non-inventory blobs always cache-render plainly.
 */
export function computeInventoryRenderPlan(blob: unknown, ctx: unknown): InventoryRenderPlan {
    const record = recordOf(blob);
    if (!record || !isInventoryNode(record)) return PLAN_CACHE_PLAIN;

    const possessId = String(inventoryField(record, 'possess_id') ?? '').toUpperCase();
    const casterId = String(recordOf(ctx)?.caster_id ?? '').toUpperCase();
    const possessedByViewer = possessId !== '' && casterId !== '' && possessId === casterId;

    // Narrow islands never force the whole card through PHP. Book action chrome uses the
    // held object state; the native activity bridge previews reading/history permissions.
    // Inventory↔encounter links retain their island and browser projection.

    // Locked (lockpick): item is locked (status on target) or it IS the lock holding "locked" on a
    // chest (status as caster).
    // Locked rows hydrate the lock_controls island.

    // Gathering resource (gather / skin): the gather button only renders when the item is NOT possessed
    // by a creature/PC/EC (lying on the ground / in a container / location) OR it's an integrated
    // monster_bit being skinned. Food/resources sitting in a creature's pack carry a gathering roll_type
    // but show NO gather button. The browser's shared action-chrome helper decides this;
    // only excavation fetches raw saved attempt facts for its resume wording.

    // Disenchant: shows for a valuable (cost ≥ 100), non-priceless item the viewing caster possesses,
    // when they have the enchanting tools. This NEVER forces a server fallback — it only decides whether
    // to paint the disenchant BUTTON. Tools CARRIED → show it. Tools not carried → omit it and still
    // cache-render instantly. (The PHP also accepts tools sitting WITHIN REACH in the encounter, but that
    // needs encounter-inventory + position data the cached blob doesn't carry; rather than slow-path every
    // valuable owned item to cover that exotic case, we just omit the button — the caster can carry the
    // tools, or use the full item page.)
    const costGp = Number(layerOf(record, 'cost_gp') ?? 0);
    if (costGp >= 100 && possessedByViewer) {
        const casterArr = findCasterArrByEcId(casterId);
        const carriesTools = !!(casterArr && casterCarriesItem(casterArr, ENCHANTING_TOOLS_ITEM_ID));
        return { server: false, showDisenchant: carriesTools };
    }

    return PLAN_CACHE_PLAIN;
}
